import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetMonitoringMessagesUpdatesQuery,
  GetMonitoringMessagesUpdatesData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetMonitoringMessagesUpdatesState
  extends MergeRequestState<GetMonitoringMessagesUpdatesData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getMonitoringMessagesUpdates,
  isFetching: (state: ReduxState) =>
    state.server.getMonitoringMessagesUpdates.isFetching,
  data: (state: ReduxState) => state.server.getMonitoringMessagesUpdates.data,
  error: (state: ReduxState) => state.server.getMonitoringMessagesUpdates.error,
};

const initialState: GetMonitoringMessagesUpdatesState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getMonitoringMessagesUpdates';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      query,
      config,
    }: { query: GetMonitoringMessagesUpdatesQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) =>
    serverApi
      .getMonitoringMessagesUpdates(query, config)
      .catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetMonitoringMessagesUpdates {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getMonitoringMessagesUpdates: ServerGetMonitoringMessagesUpdates = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
